// src/components/roads/page/RoadInventoryTable.js
// --- UPDATE: v5.0 — Road Inventory table extracted from RoadWorksPage and redesigned.
//   • Sortable headers with direction icons + aria-sort, sticky header, zebra hover
//   • Tri-state select-all checkbox (selects the rows currently visible)
//   • Debounced search (handled by useRoadWorksData) with "searching…" hint, Enter = apply now
//   • Quick filters: Critical condition · Maintenance required · AI risk (RUL ≤ 2y) · ML-starved
//   • Active filter chips (removable) and one-click "Clear all"
//   • AI RUL band summary bar for the page — click a band to filter rows by predicted risk
//   • Skeleton rows on first load, slim progress bar on background refresh (data stays visible)
//   • Density toggle, page-size selector, numbered pagination, jump-to-page, "Showing x–y of N"
//   • Client-side CSV export of the current page view, bulk bar with AI Auto-Sync
//   • Error state with retry; empty state with clear-filters action
// Rows are still rendered by the existing <RoadTableRow /> with unchanged props.
// --- UPDATE (v5.1): "AI Prediction" column (RUL · risk index · readiness) with expandable
//   per-row AI analysis; onRunAI / onMapRoad / isRunningAI forwarded to rows; CSV export adds
//   ai_source, risk index/level, confidence, readiness and the top recommended action.
// --- END UPDATE
// --- UPDATE (Road Works report tab): optional onOpenReport forwarded to rows ("Works report").
// --- END UPDATE
import React, { memo, useCallback, useMemo, useState } from 'react';
import {
    Badge, Box, Button, ButtonGroup, Checkbox, Flex, HStack, Icon, IconButton, Input,
    InputGroup, InputLeftElement, InputRightElement, Progress, Select, Skeleton, Spinner,
    Table, Tbody, Td, Text, Th, Thead, Tooltip, Tr, Wrap, WrapItem, Tag, TagLabel,
    TagCloseButton, useColorModeValue,
} from '@chakra-ui/react';
import { SearchIcon, CloseIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
    MdRoute, MdFilterList, MdBolt, MdOutlineAutoAwesome, MdArrowUpward, MdArrowDownward,
    MdUnfoldMore, MdWarning, MdBuild, MdFileDownload, MdViewHeadline, MdViewStream, MdError,
    MdRefresh,
} from 'react-icons/md';
import { FaRoad } from 'react-icons/fa';
import { AnimatePresence } from 'framer-motion';
import RoadTableRow from '../RoadTableRow';
import {
    ANIM, MotionBox, SectionCard, PanelHeader, EmptyState, RUL_BANDS, rulBand,
} from './shared';
import { AiChip, ModelTag, AiForecastIcon, MlModelIcon, RulGaugeIcon } from './AiMlIcons';
import { PAGE_SIZE_OPTIONS } from '../../../hooks/useRoadWorksData';
// --- UPDATE: AI prediction layer
import { FieldHint } from '../AiRoadHelper';
import { getRoadPrediction } from '../roadAiPredictions';
// --- END UPDATE

const COLUMN_COUNT = 10;

// ─────────────────────────────────────────────────────────────────────────────
// Sortable header
// ─────────────────────────────────────────────────────────────────────────────
const SortableTh = memo(({ col, label, sortBy, sortDir, onSort, isNumeric, activeColor }) => {
    const active = sortBy === col;
    const ariaSort = active ? (sortDir === 'ASC' ? 'ascending' : 'descending') : 'none';
    return (
        <Th isNumeric={isNumeric} aria-sort={ariaSort} p={0}>
            <Flex
                as="button" type="button" onClick={() => onSort(col)}
                w="100%" px={4} py={3} gap={1} align="center"
                justify={isNumeric ? 'flex-end' : 'flex-start'}
                color={active ? activeColor : 'inherit'}
                fontWeight="800" letterSpacing="wider" textTransform="uppercase" fontSize="10px"
                _hover={{ color: activeColor }} transition="color 0.15s"
                _focusVisible={{ outline: '2px solid', outlineColor: 'blue.400', outlineOffset: '-2px' }}
                title={`Sort by ${label}`}
            >
                {label}
                <Icon
                    as={active ? (sortDir === 'ASC' ? MdArrowUpward : MdArrowDownward) : MdUnfoldMore}
                    boxSize={3.5} opacity={active ? 1 : 0.45}
                />
            </Flex>
        </Th>
    );
});
SortableTh.displayName = 'SortableTh';

const PlainTh = ({ children, isNumeric, ...rest }) => (
    <Th isNumeric={isNumeric} py={3} fontSize="10px" fontWeight="800" letterSpacing="wider" {...rest}>{children}</Th>
);

// ─────────────────────────────────────────────────────────────────────────────
// Skeleton rows
// ─────────────────────────────────────────────────────────────────────────────
const SkeletonRows = memo(({ rows = 8 }) => (
    <>
        {Array.from({ length: rows }, (_, r) => (
            <Tr key={r}>
                {Array.from({ length: COLUMN_COUNT }, (_, c) => (
                    <Td key={c} py={3.5}>
                        <Skeleton h="12px" borderRadius="md"
                            w={c === 0 ? '16px' : c === 2 ? '85%' : `${45 + ((r * 7 + c * 13) % 40)}%`} />
                    </Td>
                ))}
            </Tr>
        ))}
    </>
));
SkeletonRows.displayName = 'SkeletonRows';

// ─────────────────────────────────────────────────────────────────────────────
// RUL band summary
// ─────────────────────────────────────────────────────────────────────────────
const RulBandBar = memo(({ roads, activeBand, onSelectBand }) => {
    const track = useColorModeValue('gray.100', 'whiteAlpha.200');
    const muted = useColorModeValue('gray.500', 'gray.400');

    const counts = useMemo(() => {
        const c = { critical: 0, poor: 0, fair: 0, healthy: 0, unscored: 0 };
        roads.forEach(r => {
            const b = rulBand(r.ai_rul?.predicted_rul_years);
            c[b ? b.key : 'unscored'] += 1;
        });
        return c;
    }, [roads]);

    const total = roads.length || 1;
    const scored = roads.length - counts.unscored;

    return (
        <Box>
            <Flex justify="space-between" align="center" mb={1.5} gap={2} wrap="wrap">
                <HStack spacing={2}>
                    <Icon as={RulGaugeIcon} color="purple.500" boxSize={4} />
                    <Text fontSize="xs" fontWeight="800">Predicted remaining life — this page</Text>
                    <ModelTag model="rsf" />
                </HStack>
                <Text fontSize="10px" color={muted}>
                    {scored}/{roads.length} scored{activeBand ? ' · click band again to clear' : ' · click a band to filter'}
                </Text>
            </Flex>
            <Flex h="10px" borderRadius="full" overflow="hidden" bg={track} role="list" aria-label="RUL risk distribution">
                {RUL_BANDS.map(b => counts[b.key] > 0 && (
                    <Tooltip key={b.key} hasArrow label={`${b.label}: ${counts[b.key]} road(s) ${b.max === Infinity ? '> 8y' : `≤ ${b.max}y`}`}>
                        <Box
                            as="button" type="button" role="listitem"
                            aria-pressed={activeBand === b.key}
                            aria-label={`${b.label} ${counts[b.key]}`}
                            w={`${(counts[b.key] / total) * 100}%`} bg={b.hex}
                            opacity={activeBand && activeBand !== b.key ? 0.3 : 1}
                            transition="all 0.25s" _hover={{ filter: 'brightness(1.1)' }}
                            onClick={() => onSelectBand(activeBand === b.key ? null : b.key)}
                        />
                    </Tooltip>
                ))}
            </Flex>
            <Wrap spacing={3} mt={1.5}>
                {RUL_BANDS.map(b => (
                    <WrapItem key={b.key}>
                        <HStack spacing={1}>
                            <Box w="8px" h="8px" borderRadius="full" bg={b.hex} />
                            <Text fontSize="10px" color={muted}>{b.label} <b>{counts[b.key]}</b></Text>
                        </HStack>
                    </WrapItem>
                ))}
                {counts.unscored > 0 && (
                    <WrapItem>
                        <HStack spacing={1}>
                            <Box w="8px" h="8px" borderRadius="full" bg="gray.300" />
                            <Text fontSize="10px" color={muted}>Unscored <b>{counts.unscored}</b></Text>
                        </HStack>
                    </WrapItem>
                )}
            </Wrap>
        </Box>
    );
});
RulBandBar.displayName = 'RulBandBar';

// ─────────────────────────────────────────────────────────────────────────────
// Pagination
// ─────────────────────────────────────────────────────────────────────────────
const pageWindow = (page, totalPages) => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const out = [1];
    const start = Math.max(2, page - 1);
    const end = Math.min(totalPages - 1, page + 1);
    if (start > 2) out.push('…l');
    for (let p = start; p <= end; p++) out.push(p);
    if (end < totalPages - 1) out.push('…r');
    out.push(totalPages);
    return out;
};

const csvCell = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// ─────────────────────────────────────────────────────────────────────────────
// Main table
// ─────────────────────────────────────────────────────────────────────────────
const RoadInventoryTable = ({
    roads, isLoading, isSearchPending, fetchError, onRetry, lookups,
    totalRoads, totalKm, page, setPage, limit, setLimit,
    sortBy, sortDir, onSort,
    search, setSearch, flushSearch,
    statusFilter, setStatusFilter, classificationFilter, setClassificationFilter,
    conditionFilter, setConditionFilter, showOnlyStarved, setShowOnlyStarved,
    hasActiveFilters, clearFilters,
    rulFilter, setRulFilter,
    selected, onToggle, onSelectIds, onClearSelection,
    bulkStatus, setBulkStatus, onBulkStatusUpdate, isBulkRunning, onOpenAiSync,
    onView, onEdit, onCreateWO, onAssess, onScheduleInsp, onReportIssue, onDelete,
    // --- UPDATE: AI wiring passed through to each row (optional)
    onRunAI, onMapRoad, isRunningAI,
    // --- END UPDATE
    onOpenReport, // --- UPDATE: Road Works report tab (optional)
}) => {
    // ── Theme (hoisted) ──
    const cardBg       = useColorModeValue('white', 'gray.800');
    const borderColor  = useColorModeValue('gray.200', 'gray.700');
    const mutedColor   = useColorModeValue('gray.500', 'gray.400');
    const filterBarBg  = useColorModeValue('gray.50', 'gray.750');
    const tableHeadBg  = useColorModeValue('gray.50', 'gray.700');
    const headColor    = useColorModeValue('gray.600', 'gray.300');
    const activeSortColor = useColorModeValue('blue.600', 'blue.300');
    const rowHoverBg   = useColorModeValue('blue.50', 'whiteAlpha.50');
    const stripeBg     = useColorModeValue('gray.50', 'whiteAlpha.50');
    const bulkBg       = useColorModeValue('blue.50', 'rgba(49,130,206,0.12)');

    const [density, setDensity] = useState('sm');
    const [jumpValue, setJumpValue] = useState('');

    // Client-side AI risk filter applies to the loaded page only
    const visibleRoads = useMemo(() => {
        if (!rulFilter) return roads;
        return roads.filter(r => rulBand(r.ai_rul?.predicted_rul_years)?.key === rulFilter);
    }, [roads, rulFilter]);

    const visibleIds = useMemo(() => visibleRoads.map(r => r.id), [visibleRoads]);
    const selectedVisible = useMemo(
        () => visibleIds.reduce((n, id) => n + (selected.has(id) ? 1 : 0), 0),
        [visibleIds, selected]
    );
    const allChecked = visibleIds.length > 0 && selectedVisible === visibleIds.length;
    const someChecked = selectedVisible > 0 && !allChecked;

    const handleSelectAll = useCallback(() => {
        onSelectIds(visibleIds, allChecked ? 'remove' : 'add');
    }, [allChecked, visibleIds, onSelectIds]);

    const totalPages = Math.max(1, Math.ceil((totalRoads || 0) / limit));
    const firstIdx = totalRoads ? (page - 1) * limit + 1 : 0;
    const lastIdx = Math.min(totalRoads || 0, (page - 1) * limit + roads.length);
    const pages = useMemo(() => pageWindow(page, totalPages), [page, totalPages]);

    const goToPage = useCallback((p) => {
        const n = Math.min(totalPages, Math.max(1, Number(p) || 1));
        if (n !== page) setPage(n);
    }, [page, totalPages, setPage]);

    // Quick filters
    const quickFilters = useMemo(() => [
        {
            key: 'critical', label: 'Critical condition', icon: MdWarning, color: 'red',
            active: conditionFilter === 'critical',
            onClick: () => setConditionFilter(conditionFilter === 'critical' ? '' : 'critical'),
        },
        {
            key: 'maint', label: 'Maintenance required', icon: MdBuild, color: 'orange',
            active: statusFilter === 'maintenance_required',
            onClick: () => setStatusFilter(statusFilter === 'maintenance_required' ? '' : 'maintenance_required'),
        },
        {
            key: 'airisk', label: 'AI risk ≤ 2y', icon: AiForecastIcon, color: 'purple', ai: true,
            active: rulFilter === 'critical',
            onClick: () => setRulFilter(rulFilter === 'critical' ? null : 'critical'),
            tooltip: 'Random Survival Forest predicts ≤ 2 years remaining life (current page)',
        },
        {
            key: 'starved', label: 'ML-starved', icon: MlModelIcon, color: 'cyan', ai: true,
            active: showOnlyStarved,
            onClick: () => setShowOnlyStarved(!showOnlyStarved),
            tooltip: 'Roads missing geometry/feature data the ML models need',
        },
    ], [conditionFilter, statusFilter, rulFilter, showOnlyStarved,
        setConditionFilter, setStatusFilter, setRulFilter, setShowOnlyStarved]);

    // Removable chips for whatever is active
    const chips = useMemo(() => {
        const c = [];
        if (search) c.push({ k: 'search', label: `Search: "${search}"`, clear: () => setSearch('') });
        if (statusFilter) c.push({ k: 'status', label: `Status: ${statusFilter.replace(/_/g, ' ')}`, clear: () => setStatusFilter('') });
        if (classificationFilter) c.push({ k: 'class', label: `Class: ${classificationFilter}`, clear: () => setClassificationFilter('') });
        if (conditionFilter) c.push({ k: 'cond', label: `Condition: ${conditionFilter}`, clear: () => setConditionFilter('') });
        if (showOnlyStarved) c.push({ k: 'starved', label: 'ML-starved only', clear: () => setShowOnlyStarved(false) });
        if (rulFilter) c.push({ k: 'rul', label: `AI RUL band: ${rulFilter}`, clear: () => setRulFilter(null), ai: true });
        return c;
    }, [search, statusFilter, classificationFilter, conditionFilter, showOnlyStarved, rulFilter,
        setSearch, setStatusFilter, setClassificationFilter, setConditionFilter, setShowOnlyStarved, setRulFilter]);

    const clearAll = useCallback(() => { clearFilters(); setRulFilter(null); }, [clearFilters, setRulFilter]);

    const exportPage = useCallback(() => {
        const header = ['id', 'road_code', 'name', 'constituency', 'classification', 'condition_rating',
            'condition_score', 'status', 'length_m', 'ai_rul_years', 'ai_rul_band',
            // --- UPDATE
            'ai_source', 'ai_risk_index', 'ai_risk_level', 'ai_confidence', 'ai_readiness', 'ai_recommended_action'];
            // --- END UPDATE
        const lines = visibleRoads.map(r => {
            const rul = r.ai_rul?.predicted_rul_years;
            // --- UPDATE
            const p = getRoadPrediction(r);
            // --- END UPDATE
            return [
                r.id, r.road_code || r.metadata?.road_code, r.name, r.constituency, r.classification,
                r.condition_rating, r.condition_score, r.status, r.metadata?.length_m,
                rul != null ? Number(rul).toFixed(2) : '', rulBand(rul)?.label || '',
                // --- UPDATE
                p.source, p.riskScore, p.riskLevel.label,
                p.confidence != null ? Math.round(p.confidence * 100) : '', p.readiness.score, p.actions[0]?.label || '',
                // --- END UPDATE
            ].map(csvCell).join(',');
        });
        const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `roads-page-${page}-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, [visibleRoads, page]);

    const showSkeleton = isLoading && roads.length === 0;
    const showBgProgress = isLoading && roads.length > 0;

    return (
        <SectionCard accentColor="blue" hoverLift={false}>
            <PanelHeader
                iconBg="blue.500" icon={MdRoute}
                title="Road Inventory"
                badge={<AiChip kind="ml" label="RUL" size="xs" tooltip="AI remaining-useful-life scores merged into each row" />}
                subtitle={`${totalRoads} total · ${roads.length} on this page · ${totalKm} km visible`}
                right={
                    <HStack spacing={2}>
                        {selected.size > 0 && (
                            <MotionBox initial={{ scale: 0 }} animate={{ scale: 1 }} transition={ANIM.springBounce}>
                                <Badge colorScheme="blue" borderRadius="full" px={2} fontSize="xs">
                                    {selected.size} selected
                                </Badge>
                            </MotionBox>
                        )}
                        <ButtonGroup size="xs" isAttached variant="outline">
                            <Tooltip label="Compact rows" hasArrow>
                                <IconButton aria-label="Compact rows" icon={<Icon as={MdViewHeadline} />}
                                    isActive={density === 'sm'} onClick={() => setDensity('sm')} />
                            </Tooltip>
                            <Tooltip label="Comfortable rows" hasArrow>
                                <IconButton aria-label="Comfortable rows" icon={<Icon as={MdViewStream} />}
                                    isActive={density === 'md'} onClick={() => setDensity('md')} />
                            </Tooltip>
                        </ButtonGroup>
                        <Tooltip label="Download the rows currently shown as CSV" hasArrow>
                            <IconButton size="xs" variant="outline" aria-label="Export this page"
                                icon={<Icon as={MdFileDownload} />} onClick={exportPage}
                                isDisabled={!visibleRoads.length} />
                        </Tooltip>
                    </HStack>
                }
            />

            {/* FILTER BAR */}
            <Box p={4} borderBottomWidth="1px" borderColor={borderColor} bg={filterBarBg}>
                <Flex gap={3} wrap="wrap" align="center">
                    <InputGroup size="sm" w={{ base: 'full', md: '280px' }}>
                        <InputLeftElement pointerEvents="none">
                            {isSearchPending
                                ? <Spinner size="xs" color="blue.400" />
                                : <Icon as={SearchIcon} color="gray.400" boxSize={3} />}
                        </InputLeftElement>
                        <Input placeholder="Search road name or code…"
                            aria-label="Search roads"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') flushSearch(); if (e.key === 'Escape') setSearch(''); }}
                            bg={cardBg} borderRadius="lg" />
                        {search && (
                            <InputRightElement>
                                <IconButton size="xs" variant="ghost" aria-label="Clear search"
                                    icon={<CloseIcon boxSize={2} />} onClick={() => setSearch('')} />
                            </InputRightElement>
                        )}
                    </InputGroup>
                    {lookups?.statuses && (
                        <Select size="sm" w={{ base: 'full', md: '150px' }} aria-label="Status filter"
                            placeholder="All Statuses" value={statusFilter}
                            onChange={e => setStatusFilter(e.target.value)} bg={cardBg} borderRadius="lg">
                            {lookups.statuses.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ').toUpperCase()}</option>)}
                        </Select>
                    )}
                    {lookups?.classifications && (
                        <Select size="sm" w={{ base: 'full', md: '150px' }} aria-label="Classification filter"
                            placeholder="All Classes" value={classificationFilter}
                            onChange={e => setClassificationFilter(e.target.value)} bg={cardBg} borderRadius="lg">
                            {lookups.classifications.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
                        </Select>
                    )}
                    {lookups?.condition_ratings && (
                        <Select size="sm" w={{ base: 'full', md: '150px' }} aria-label="Condition filter"
                            placeholder="All Conditions" value={conditionFilter}
                            onChange={e => setConditionFilter(e.target.value)} bg={cardBg} borderRadius="lg">
                            {lookups.condition_ratings.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
                        </Select>
                    )}
                    <Button size="sm" colorScheme="blue" leftIcon={<Icon as={MdFilterList} />}
                        onClick={flushSearch} borderRadius="lg">
                        Apply
                    </Button>
                    {(hasActiveFilters || rulFilter) && (
                        <Button size="sm" variant="ghost" onClick={clearAll}>Clear all</Button>
                    )}
                </Flex>

                {/* Quick filters */}
                <Wrap spacing={2} mt={3}>
                    {quickFilters.map(q => {
                        const btn = (
                            <Button size="xs" borderRadius="full"
                                variant={q.active ? 'solid' : 'outline'}
                                colorScheme={q.color}
                                leftIcon={<Icon as={q.icon} boxSize={3.5} />}
                                aria-pressed={q.active}
                                onClick={q.onClick}>
                                {q.label}
                                {q.ai && <Box as="span" ml={1.5}><AiChip kind={q.key === 'airisk' ? 'forecast' : 'ml'} size="xs" label={q.key === 'airisk' ? 'AI' : 'ML'} /></Box>}
                            </Button>
                        );
                        return (
                            <WrapItem key={q.key}>
                                {q.tooltip ? <Tooltip label={q.tooltip} hasArrow>{btn}</Tooltip> : btn}
                            </WrapItem>
                        );
                    })}
                </Wrap>

                {/* Active filter chips */}
                {chips.length > 0 && (
                    <Wrap spacing={2} mt={3}>
                        {chips.map(c => (
                            <WrapItem key={c.k}>
                                <Tag size="sm" borderRadius="full" variant="subtle" colorScheme={c.ai ? 'purple' : 'blue'}>
                                    <TagLabel textTransform="capitalize">{c.label}</TagLabel>
                                    <TagCloseButton aria-label={`Remove ${c.label}`} onClick={c.clear} />
                                </Tag>
                            </WrapItem>
                        ))}
                    </Wrap>
                )}

                {/* RUL band summary */}
                {roads.length > 0 && (
                    <Box mt={4}>
                        <RulBandBar roads={roads} activeBand={rulFilter} onSelectBand={setRulFilter} />
                    </Box>
                )}

                {/* Bulk action bar */}
                <AnimatePresence>
                    {selected.size > 0 && lookups?.statuses && (
                        <MotionBox
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            mt={3} overflow="hidden"
                        >
                            <Flex gap={2} wrap="wrap" align="center" p={2.5} borderRadius="xl" bg={bulkBg}
                                borderWidth="1px" borderColor="blue.200">
                                <Text fontSize="xs" color={mutedColor} fontWeight="700">
                                    Bulk action · {selected.size} road{selected.size === 1 ? '' : 's'}:
                                </Text>
                                <Select size="sm" w="200px" placeholder="Set status to…"
                                    aria-label="Bulk status" value={bulkStatus}
                                    onChange={e => setBulkStatus(e.target.value)} bg={cardBg} borderRadius="lg">
                                    {lookups.statuses.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                                </Select>
                                <Button size="sm" colorScheme="orange" leftIcon={<Icon as={MdBolt} />}
                                    onClick={onBulkStatusUpdate} isLoading={isBulkRunning}
                                    isDisabled={!bulkStatus} borderRadius="lg">
                                    Apply to {selected.size}
                                </Button>
                                <Button size="sm" color="white" bgGradient="linear(to-r, purple.500, pink.500)"
                                    _hover={{ bgGradient: 'linear(to-r, purple.600, pink.600)' }}
                                    leftIcon={<Icon as={MdOutlineAutoAwesome} />}
                                    onClick={onOpenAiSync} borderRadius="lg">
                                    AI Auto-Sync State
                                </Button>
                                <Button size="sm" variant="ghost" onClick={onClearSelection}>
                                    Clear selection
                                </Button>
                            </Flex>
                        </MotionBox>
                    )}
                </AnimatePresence>
            </Box>

            {/* TABLE */}
            <Box position="relative">
                {showBgProgress && (
                    <Progress size="xs" isIndeterminate colorScheme="blue"
                        position="absolute" top={0} left={0} right={0} zIndex={3} aria-label="Refreshing" />
                )}
                <Box overflowX="auto" maxH="70vh" overflowY="auto"
                    opacity={showBgProgress ? 0.75 : 1} transition="opacity 0.2s">
                    {fetchError && roads.length === 0 && !isLoading ? (
                        <EmptyState icon={MdError} title="Couldn't load roads"
                            subtitle={fetchError.message || 'The server did not respond.'}
                            action={<Button size="sm" leftIcon={<Icon as={MdRefresh} />} colorScheme="blue" onClick={() => onRetry()}>Retry</Button>} />
                    ) : (
                        <Table variant="simple" size={density}
                            sx={{
                                'tbody tr': { transition: 'background 0.15s' },
                                'tbody tr:nth-of-type(even)': { bg: stripeBg },
                                'tbody tr:hover': { bg: rowHoverBg },
                            }}>
                            <Thead bg={tableHeadBg} position="sticky" top={0} zIndex={2}
                                boxShadow={`inset 0 -1px 0 var(--chakra-colors-${borderColor.replace('.', '-')})`}
                                sx={{ th: { color: headColor } }}>
                                <Tr>
                                    <Th py={3} w="40px">
                                        <Checkbox
                                            aria-label={allChecked ? 'Deselect all rows' : 'Select all rows'}
                                            isChecked={allChecked} isIndeterminate={someChecked}
                                            onChange={handleSelectAll} isDisabled={!visibleIds.length}
                                            colorScheme="blue" />
                                    </Th>
                                    <PlainTh>Road Code</PlainTh>
                                    <SortableTh col="name" label="Name & Extent" sortBy={sortBy} sortDir={sortDir} onSort={onSort} activeColor={activeSortColor} />
                                    <PlainTh>Constituency</PlainTh>
                                    <SortableTh col="condition" label="Condition" sortBy={sortBy} sortDir={sortDir} onSort={onSort} activeColor={activeSortColor} />
                                    <PlainTh isNumeric>
                                        <HStack spacing={1} justify="flex-end">
                                            <Icon as={RulGaugeIcon} boxSize={3.5} color="purple.400" />
                                            {/* --- UPDATE: RUL · risk · readiness */}
                                            <Tooltip hasArrow label="Remaining life (years) · risk index · data readiness. Click a cell or the ▸ arrow for the full AI explanation.">
                                                <span>AI Prediction</span>
                                            </Tooltip>
                                            <FieldHint metric="risk_index" />
                                            {/* --- END UPDATE */}
                                        </HStack>
                                    </PlainTh>
                                    <SortableTh col="length_m" label="Length (m)" isNumeric sortBy={sortBy} sortDir={sortDir} onSort={onSort} activeColor={activeSortColor} />
                                    <PlainTh>Next Insp.</PlainTh>
                                    <SortableTh col="status" label="Status" sortBy={sortBy} sortDir={sortDir} onSort={onSort} activeColor={activeSortColor} />
                                    <PlainTh>Actions</PlainTh>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {showSkeleton ? (
                                    <SkeletonRows rows={8} />
                                ) : (
                                    visibleRoads.map((road, i) => (
                                        <RoadTableRow
                                            key={road.id}
                                            road={road}
                                            index={i}
                                            isSelected={selected.has(road.id)}
                                            onToggle={onToggle}
                                            onView={onView}
                                            onEdit={onEdit}
                                            onCreateWO={onCreateWO}
                                            onAssess={onAssess}
                                            onScheduleInsp={onScheduleInsp}
                                            onReportIssue={onReportIssue}
                                            onDelete={onDelete}
                                            // --- UPDATE
                                            onRunAI={onRunAI}
                                            onMapRoad={onMapRoad}
                                            isRunningAI={isRunningAI}
                                            columnCount={COLUMN_COUNT}
                                            // --- END UPDATE
                                            onOpenReport={onOpenReport} // --- UPDATE
                                        />
                                    ))
                                )}
                                {!showSkeleton && visibleRoads.length === 0 && !isLoading && (
                                    <Tr>
                                        <Td colSpan={COLUMN_COUNT}>
                                            <EmptyState icon={FaRoad}
                                                title={rulFilter && roads.length ? 'No roads in this AI risk band' : 'No road segments found'}
                                                subtitle={rulFilter && roads.length
                                                    ? 'No road on this page falls in the selected RUL band.'
                                                    : 'Try adjusting your filters or add a new road'}
                                                action={(hasActiveFilters || rulFilter) && (
                                                    <Button size="sm" variant="outline" colorScheme="blue" onClick={clearAll}>
                                                        Clear all filters
                                                    </Button>
                                                )} />
                                        </Td>
                                    </Tr>
                                )}
                            </Tbody>
                        </Table>
                    )}
                </Box>
            </Box>

            {/* PAGINATION */}
            <Flex justify="space-between" align="center" p={4} gap={3} wrap="wrap"
                borderTopWidth="1px" borderColor={borderColor}>
                <HStack spacing={3}>
                    <Text fontSize="sm" color={mutedColor}>
                        {totalRoads ? <>Showing <b>{firstIdx}–{lastIdx}</b> of <b>{totalRoads}</b></> : 'No results'}
                        {rulFilter && ` · ${visibleRoads.length} match AI filter`}
                    </Text>
                    <Select size="xs" w="auto" borderRadius="md" value={limit} aria-label="Rows per page"
                        onChange={e => setLimit(Number(e.target.value))}>
                        {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n} / page</option>)}
                    </Select>
                </HStack>

                <HStack spacing={1}>
                    <Button size="sm" variant="ghost"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        isDisabled={page === 1}
                        leftIcon={<ChevronRightIcon transform="rotate(180deg)" />}>
                        Previous
                    </Button>
                    {pages.map(p => (typeof p === 'number' ? (
                        <Button key={p} size="sm" minW="32px" px={2} borderRadius="lg"
                            variant={p === page ? 'solid' : 'ghost'} colorScheme={p === page ? 'blue' : 'gray'}
                            aria-current={p === page ? 'page' : undefined}
                            onClick={() => goToPage(p)}>
                            {p}
                        </Button>
                    ) : (
                        <Text key={p} px={1} color={mutedColor} fontSize="sm">…</Text>
                    )))}
                    <Button size="sm" variant="ghost"
                        onClick={() => setPage(p => p + 1)}
                        isDisabled={roads.length < limit || page >= totalPages}
                        rightIcon={<ChevronRightIcon />}>
                        Next
                    </Button>
                </HStack>

                {totalPages > 7 && (
                    <HStack spacing={1}>
                        <Text fontSize="xs" color={mutedColor}>Go to</Text>
                        <Input size="xs" w="56px" borderRadius="md" type="number" min={1} max={totalPages}
                            aria-label="Jump to page" value={jumpValue}
                            onChange={e => setJumpValue(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { goToPage(jumpValue); setJumpValue(''); } }} />
                    </HStack>
                )}
            </Flex>
        </SectionCard>
    );
};

export default memo(RoadInventoryTable);
